import { put, select, call } from 'redux-saga/effects'
import { updateStoreLocator } from '../../actionCreators/storeLocator/storeLocatorActionCreators'
import { fetchApi } from '../helpers/fetchApi'
import Logger from '../../helpers/Logger'

const logger = Logger.getInstance()
const endpoint = '/stores/api/orchestrator/v0/cis-locator/availability'
let timeout = 3000

/**
 * Get "origin" value either from either the payload or the store ID supplied via Google Ads
 */
const getOrigin = (payload, storeLocator) => payload.origin || storeLocator.data.storeNumber || ''

/**
 * Get "postcode" value either from either the payload or ???
 */
const getPostcode = (payload, storeLocator) => payload.postcode || storeLocator.data.deliveryPostcode || ''

const getQuery = (payload, storeLocator) => {
  const postcode = getPostcode(payload, storeLocator)
  const origin = getOrigin(payload, storeLocator)
  const { partNumber, quantity } = payload

  const query = {
    maxDistance: 50,
    maxResults: 10,
    skuQty: `${partNumber}_${quantity}`,
    channel: 'web_pdp',
    timestamp: Date.now()
  }

  /**
   * Send neither origin nor postcode to AO when changing quantity - AO to use sessionId cookie
   * Also, set timeout to 10secs to accommodate delayed response from IM
   */
  if (payload.quantityChangeRequest) {
    timeout = 10000
    return query
  }
  if (payload.deliveryRequest) return { ...query, postcode }
  if (origin) return { ...query, origin }
  return query
}

const storeLocatorSelector = state => state.storeLocator

const validateFulfilmentResponse = responseObj => {
  const storeCollectionObj = (responseObj.stores && responseObj.stores[0]) || ''
  const deliveryObj = (responseObj.delivery && responseObj.delivery[0]) || ''
  const partNumber = storeCollectionObj.availability && storeCollectionObj.availability[0].sku
  return { storeCollectionObj, deliveryObj, partNumber }
}

const getDeliveryDetails = (deliveryObj, partNumber) => {
  const deliveryDetails = {}
  const deliveryMessage = deliveryObj.messages[partNumber]
  deliveryDetails.deliveryError = false
  deliveryDetails.deliveryMessage = deliveryMessage.text
  deliveryDetails.deliveryIcon = deliveryMessage.icon
  deliveryDetails.deliveryCode = deliveryMessage.analyticsCode
  deliveryDetails.deliveryMessageKey = deliveryMessage.messageKey
  deliveryDetails.deliveryPostcode = deliveryObj.postcode === 'UNDEFINED' ? '' : deliveryObj.postcode
  deliveryDetails.deliveryLoading = false
  deliveryDetails.deliveryUpdateTimestamp = Date.now()
  return deliveryDetails
}

const getCollectionDetails = (collectionObj, partNumber, searchTerm = '') => {
  const collectionDetails = {}
  const { postcode, store_id: storeId = '', legacy_name: legacyName = '' } = collectionObj.storeinfo
  collectionDetails.collectionMessage = collectionObj.messages.collection_summary.text
  collectionDetails.collectionIcon = collectionObj.messages.collection_summary.icon
  collectionDetails.collectionStore = legacyName
  collectionDetails.collectionStoreDistance = collectionObj.distance || 0
  collectionDetails.currentSearchTerm = searchTerm || localStorage.getItem('searchTerm') || postcode
  collectionDetails.storeNumber = storeId
  collectionDetails.collectionStockCode = collectionObj.messages[partNumber].analyticsCode
  collectionDetails.collectionCutoff = collectionObj?.availability[0]?.cutoff || ''
  collectionDetails.collectionMessageKey = collectionObj.messages[partNumber].messageKey
  collectionDetails.convenienceStore = collectionObj.storeinfo.locator_settings
  collectionDetails.collectionUpdateTimestamp = Date.now()
  collectionDetails.alternativeStore = false
  collectionDetails.fetchAlternativeStore = false
  collectionDetails.stockCheckAvailable = true
  collectionDetails.collectionLoading = false
  collectionDetails.deliveryLoading = false
  collectionDetails.alternativeStoreSelected = false
  return collectionDetails
}

const shouldRetryLocaliseFulfilment = payload => {
  if (!payload.stores || (payload.stores && payload.stores.length > 1)) return false
  const store = payload.stores[0]
  const partNumber = store.availability[0].sku
  return store.messages[partNumber].messageKey !== 'COLLECTION_NOW'
}

function InvalidStoreException() {
  this.message = 'Invalid Store Details Exception'
}

const getUpdateFulfilmentMessagePayload = (
  payload,
  fetchAlternativeStore,
  searchTerm = '',
  choseAlternativeStore = false
) => {
  const { storeCollectionObj, deliveryObj, partNumber } = validateFulfilmentResponse(payload)

  if (!storeCollectionObj && deliveryObj && deliveryObj.messages) {
    throw new InvalidStoreException()
  }

  const deliveryDetails = deliveryObj && deliveryObj.messages && getDeliveryDetails(deliveryObj, partNumber)

  const collectionDetails = storeCollectionObj && getCollectionDetails(storeCollectionObj, partNumber, searchTerm)

  const fulfilmentPayload = { ...collectionDetails, ...deliveryDetails, isLocalised: true }

  if (fetchAlternativeStore) fulfilmentPayload.fetchAlternativeStore = true
  if (choseAlternativeStore) {
    fulfilmentPayload.alternativeStore = { storeId: fulfilmentPayload.storeNumber }
    fulfilmentPayload.alternativeStoreSelected = true
  }

  fulfilmentPayload.isFinished = true

  return fulfilmentPayload
}

function* localiseFulfilmentSaga(action) {
  const { payload } = action
  const searchTerm = payload.landingPageRequest ? payload.postcode : payload.searchTerm || ''
  if (searchTerm) {
    localStorage.setItem('searchTerm', searchTerm)
  }
  const loadingStatus = payload.deliveryRequest
    ? { collectionLoading: false, deliveryLoading: true }
    : { collectionLoading: true, deliveryLoading: true }
  try {
    const storeLocator = yield select(storeLocatorSelector)
    yield put(updateStoreLocator(loadingStatus))

    const query = getQuery(payload, storeLocator)
    const localisationResponse = yield call(fetchApi, endpoint, query, {}, timeout)
    const fetchAlternativeStore =
      !payload.fromChooseAlternativeStore && shouldRetryLocaliseFulfilment(localisationResponse)

    const updatePayload = getUpdateFulfilmentMessagePayload(
      localisationResponse,
      fetchAlternativeStore,
      searchTerm,
      payload.fromChooseAlternativeStore
    )
    const updateAction = updateStoreLocator(updatePayload)
    yield put(updateAction)
  } catch (error) {
    const errorDetail = error.toString()
    logger.error('functionLogger', { payload, errorDetail }, 'Localise Fulfilment Saga error')

    /**
     * check if request happened on page load and error occurred because there's no postcode, origin,
     * sessionId, postcodeSessionIdCookie or storeLocatorStateSessionCookie or if error occurred due to invalid postcode
     */
    if (error instanceof InvalidStoreException || payload.landingPageRequest) {
      yield put(updateStoreLocator({ collectionLoading: false, deliveryLoading: false, isFinished: true }))
    } else {
      const storeSelectorFailurePayload = {
        stockCheckAvailable: false,
        collectionLoading: false,
        deliveryLoading: false,
        storeName: 'An error ocurred',
        errorDetail
      }
      const storeSelectorFailureAction = updateStoreLocator(storeSelectorFailurePayload)
      yield put(storeSelectorFailureAction)
    }
  }
}

export { localiseFulfilmentSaga, endpoint, storeLocatorSelector, getUpdateFulfilmentMessagePayload }
